import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OverBudgetAlerts = ({ categories }) => {
  const getAlertLevel = (spent, allocated) => {
    const percentage = (spent / allocated) * 100;
    if (percentage >= 100) return { level: 'critical', label: 'Over Budget', color: 'var(--color-error)', icon: 'XCircle' };
    if (percentage >= 90) return { level: 'high', label: 'Critical', color: 'var(--color-error)', icon: 'AlertCircle' };
    if (percentage >= 75) return { level: 'medium', label: 'Warning', color: 'var(--color-warning)', icon: 'AlertTriangle' };
    return { level: 'low', label: 'Normal', color: 'var(--color-success)', icon: 'CheckCircle' };
  };

  const alertCategories = categories
    ?.map(cat => ({
      ...cat,
      percentage: (cat?.spent / cat?.allocated) * 100,
      alert: getAlertLevel(cat?.spent, cat?.allocated)
    }))
    ?.filter(cat => cat?.alert?.level !== 'low')
    ?.sort((a, b) => b?.percentage - a?.percentage);

  const criticalCount = alertCategories?.filter(cat => cat?.alert?.level === 'critical')?.length;
  const highCount = alertCategories?.filter(cat => cat?.alert?.level === 'high')?.length;
  const mediumCount = alertCategories?.filter(cat => cat?.alert?.level === 'medium')?.length;

  return (
    <div className="bg-card rounded-2xl p-4 md:p-6 shadow-elevation-2">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
            <Icon name="Bell" size={20} color="var(--color-error)" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground">Budget Alerts</h2>
            <p className="text-sm text-muted-foreground">Visual warnings for spending limits</p>
          </div>
        </div>
        {alertCategories?.length > 0 && (
          <div className="w-8 h-8 rounded-full bg-error text-error-foreground flex items-center justify-center text-sm font-bold">
            {alertCategories?.length}
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="p-3 bg-error/10 rounded-lg text-center">
          <p className="text-2xl font-bold text-error">{criticalCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Critical</p>
        </div>
        <div className="p-3 bg-warning/10 rounded-lg text-center">
          <p className="text-2xl font-bold text-warning">{highCount}</p>
          <p className="text-xs text-muted-foreground mt-1">High</p>
        </div>
        <div className="p-3 bg-warning/10 rounded-lg text-center">
          <p className="text-2xl font-bold text-warning">{mediumCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Warning</p>
        </div>
      </div>

      <div className="space-y-3">
        {alertCategories?.length === 0 ? (
          <div className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
              <Icon name="CheckCircle" size={32} color="var(--color-success)" />
            </div>
            <p className="text-sm font-medium text-foreground mb-1">All Categories On Track</p>
            <p className="text-xs text-muted-foreground">No budget alerts at this time</p>
          </div>
        ) : (
          alertCategories?.map((category) => (
            <div
              key={category?.id}
              className="p-4 rounded-xl border-2 transition-smooth hover:shadow-md"
              style={{ 
                borderColor: category?.alert?.color,
                backgroundColor: `${category?.alert?.color}08`
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center" 
                    style={{ backgroundColor: `${category?.color}15` }}
                  >
                    <Icon name={category?.icon} size={20} color={category?.color} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{category?.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Icon name={category?.alert?.icon} size={14} color={category?.alert?.color} />
                      <span className="text-xs font-medium" style={{ color: category?.alert?.color }}>
                        {category?.alert?.label}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-base font-bold" style={{ color: category?.alert?.color }}>
                    {category?.percentage?.toFixed(0)}%
                  </p>
                  <p className="text-xs text-muted-foreground">of budget</p>
                </div>
              </div>

              <div className="relative w-full h-3 bg-muted rounded-full overflow-hidden mb-3">
                <div
                  className="absolute top-0 left-0 h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(category?.percentage, 100)}%`,
                    backgroundColor: category?.alert?.color
                  }}
                />
                {category?.percentage >= 75 && (
                  <div
                    className="absolute top-0 h-full w-0.5 bg-foreground/30"
                    style={{ left: '75%' }}
                  />
                )}
                {category?.percentage >= 90 && (
                  <div
                    className="absolute top-0 h-full w-0.5 bg-foreground/30"
                    style={{ left: '90%' }}
                  />
                )}
              </div>

              <div className="flex items-center justify-between text-xs mb-3">
                <span className="text-muted-foreground">
                  Spent: ${category?.spent?.toLocaleString()}
                </span>
                <span className="text-muted-foreground">
                  Budget: ${category?.allocated?.toLocaleString()}
                </span>
              </div>

              {category?.percentage >= 100 ? (
                <div className="p-2 bg-error/10 rounded-lg">
                  <p className="text-xs text-error font-medium">
                    Over budget by ${(category?.spent - category?.allocated)?.toLocaleString()}
                  </p>
                </div>
              ) : category?.percentage >= 90 ? (
                <div className="p-2 bg-warning/10 rounded-lg">
                  <p className="text-xs text-warning font-medium">
                    Only ${(category?.allocated - category?.spent)?.toLocaleString()} remaining
                  </p>
                </div>
              ) : (
                <div className="p-2 bg-warning/10 rounded-lg">
                  <p className="text-xs text-warning font-medium">
                    Approaching limit - ${(category?.allocated - category?.spent)?.toLocaleString()} left
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {alertCategories?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <Button variant="outline" fullWidth iconName="Settings" iconPosition="left">
            Configure Alert Settings
          </Button>
        </div>
      )}
    </div>
  );
};

export default OverBudgetAlerts;